/**
 * Shared response-envelope unwrap for the notifications module.
 *
 * Older fairdee-platform responses are wrapped as
 * `{status: "success"|"error", data, message?}`, but the modern
 * `BaseApiMixin.successful_get_response` writes the payload flat (no
 * wrapper). This helper handles both: if the wrapper is present, peel it;
 * otherwise return the body as-is. Each `api.ts` in this module reaches
 * for it locally — keeps modules self-contained per the rewrite README.
 */

export interface ApiEnvelope<T> {
  status?: 'success' | 'error';
  data?: T;
  message?: string;
  errors?: Record<string, string>;
}

export function unwrapEnvelope<T>(payload: ApiEnvelope<T> | T): T | undefined {
  if (payload && typeof payload === 'object') {
    const maybeEnvelope = payload as ApiEnvelope<T>;
    if (maybeEnvelope.status === 'success' && 'data' in maybeEnvelope) {
      return maybeEnvelope.data;
    }
    if (maybeEnvelope.status === 'error') {
      return undefined;
    }
  }
  return payload as T;
}

/**
 * Coerce a backend list response into the module's `{results, count}` shape.
 *
 * The notifications endpoints aren't uniform — `/notification-configs`
 * returns `{configs: [...]}`, `/notification-templates` returns
 * `{templates: [...]}`, others return `{results, count}` directly. This
 * helper accepts any of the three, prefers the row-array under the
 * caller-supplied key (`configs` / `templates`), falls back to `results`,
 * and synthesises `count` from `results.length` when the backend omits it.
 */
export interface PaginatedListLike<T> {
  results?: T[];
  count?: number;
  [key: string]: T[] | number | undefined;
}

export function normaliseListResponse<T>(
  body: PaginatedListLike<T> | undefined,
  primaryKey: string,
): { results: T[]; count: number } {
  const rows = (body?.[primaryKey] as T[] | undefined) ?? body?.results ?? [];
  return { results: rows, count: body?.count ?? rows.length };
}
